<template>
    <div class="download-modal__select-wrapper">
        Format
        <Select
            class="platform-select"
            :model-value="modelValue"
            data-testid="asset-download__format-select"
            @onChange="$emit('update:modelValue', $event)"
        >
            <Option
                v-for="(exportFormat, i) in availableExportFormats"
                :key="exportFormat"
                class="platform-select__option"
                :value="exportFormat"
                :class="{
                    'platform-select__option--divider': i > 0 && hasDivider(exportFormat)
                }"
                :data-testid="`download-modal__platform-select__option-${exportFormat}`"
            >
                {{ EXPORT_CONFIGS[exportFormat].label }}
            </Option>
        </Select>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import {
    EXPORT_FORMAT,
    EXPORT_CONFIGS,
    EXPORT_CONFIGS_BY_TEMPLATE_TYPE,
    type ExportFormatType
} from "shared-utils/enums/exports";
import { TemplateType } from "shared-utils/enums/masterTemplate";
import { USER_ACTIONS } from "shared-utils/enums/user";

import { userCan } from "@/services/Auth";

const FormatsWithDividers = [
    EXPORT_FORMAT.OPENDC,
    EXPORT_FORMAT.FLASHTALKING,
    EXPORT_FORMAT.GENERIC,
    EXPORT_FORMAT.IMAGE_PNG,
    EXPORT_FORMAT.VIDEO,
    EXPORT_FORMAT.INDESIGN_PDF
] as const;

export default defineComponent({
    props: {
        modelValue: {
            required: true,
            type: String as PropType<ExportFormatType | "">
        }
    },

    emits: ["update:modelValue"],

    data() {
        return {
            EXPORT_CONFIGS
        };
    },

    computed: {
        clientId() {
            return this.$route.params.clientId as string;
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        campaignType() {
            return this.$store.state.campaign.campaignType;
        },

        userCanDownloadVideo() {
            return userCan(USER_ACTIONS.canDownloadVideo, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        availableExportFormats() {
            switch (this.campaignType) {
                case TemplateType.AE:
                    return this.userCanDownloadVideo ? EXPORT_CONFIGS_BY_TEMPLATE_TYPE.AE : [];

                case TemplateType.PSD:
                    return EXPORT_CONFIGS_BY_TEMPLATE_TYPE.PSD;

                case TemplateType.INDESIGN:
                    return EXPORT_CONFIGS_BY_TEMPLATE_TYPE.INDESIGN;

                case TemplateType.HTML5:
                default:
                    return EXPORT_CONFIGS_BY_TEMPLATE_TYPE.HTML5.filter(downloadFormat => {
                        return this.userCanDownloadVideo || downloadFormat !== EXPORT_FORMAT.VIDEO;
                    });
            }
        }
    },

    methods: {
        hasDivider(exportFormat: ExportFormatType) {
            return FormatsWithDividers.includes(exportFormat as (typeof FormatsWithDividers)[number]);
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.download-modal__select-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 $spacing;
    width: 500px;
}

.platform-select {
    flex: 1 0;
    width: 100%;
    margin-left: 20px;
    &__option {
        border-top: 1px solid transparent;

        &--divider {
            border-top: 1px solid $grey3;
            margin-top: 5px;
            padding-top: 7px;

            &:before {
                content: "";
                height: 5px;
                display: block;
                background-color: $white;
                width: calc(100% + 2 * 16px);
                margin-left: -$spacing;
                position: relative;
                top: -7px;
            }
        }
    }
}
</style>
